import { format } from 'date-fns';
import { getRoot, Instance, types } from 'mobx-state-tree';
import { PageModel, PageRoutes, RootType } from '../../../internal';

export const KvizDetaljiModel = types
  .compose(
    PageModel,
    types.model({
      quizId: types.optional(types.number, 0),
      name: types.optional(types.string, ''),
      description: types.optional(types.string, ''),
      quizType: types.optional(types.string, ''),
      isActive: types.optional(types.boolean, false),
      startDate: types.optional(types.string, ''),
      endDate: types.optional(types.string, ''),
      resultsVisibleUntil: types.optional(types.string, ''),
      startTime: types.optional(types.string, ''),
      endTime: types.optional(types.string, ''),
      dailyWinnersCount: types.optional(types.number, 1),
    })
  )
  .views((self) => ({
    get root(): RootType {
      return getRoot(self);
    },
    get isValid(): boolean {
      return (
        !!self.name.trim() &&
        !!self.quizType &&
        !!self.startDate &&
        !!self.endDate &&
        !!self.startTime &&
        !!self.endTime
      );
    },
  }))
  .actions((self) => ({
    setName(value: string) {
      self.name = value;
    },
    setDescription(value: string) {
      self.description = value;
    },
    setQuizType(value: string) {
      self.quizType = value;
    },
    setIsActive(value: boolean) {
      self.isActive = value;
    },
    setStartDate(value: string) {
      self.startDate = value;
    },
    setEndDate(value: string) {
      self.endDate = value;
    },
    setResultsVisibleUntil(value: string) {
      self.resultsVisibleUntil = value;
    },
    setStartTime(value: string) {
      self.startTime = value;
    },
    setEndTime(value: string) {
      self.endTime = value;
    },
    setDailyWinnersCount(value: number) {
      self.dailyWinnersCount = value;
    },
    setFromApi(quiz: any) {
      self.quizId = quiz.id;
      self.name = quiz.name;
      self.description = quiz.description ?? '';
      self.quizType = quiz.quizType;
      self.isActive = quiz.isActive;
      self.startDate = quiz.startDate;
      self.endDate = quiz.endDate;
      self.startTime = quiz.startTime ?? '';
      self.endTime = quiz.endTime ?? '';
      self.resultsVisibleUntil = quiz.resultsVisibleUntil
        ? this.formatResultsVisibleUntil(quiz.resultsVisibleUntil)
        : '';
      self.dailyWinnersCount = quiz.dailyWinnersCount ?? 1;
    },
    async beforePageEnter() {
      const id = Number(self.root.router.queryParams?.id);
      if (!id) {
        (this as any).navigateBack();
        return Promise.resolve(false);
      }
      try {
        const quiz = await self.root.api.quiz.getQuiz(id);
        (this as any).setFromApi(quiz);
      } catch (e: any) {
        (this as any).navigateBack();
      }
      return Promise.resolve(true);
    },
    navigateBack() {
      self.root.router.navigate({ newView: PageRoutes.SviKvizovi.id });
    },
    navigateToPitanja() {
      self.root.router.navigate({
        newView: PageRoutes.Pitanja.id,
        queryParams: { quizId: String(self.quizId) },
      });
    },
    async submit() {
      const payload: any = {
        name: self.name.trim(),
        quizType: self.quizType,
        isActive: self.isActive,
        startDate: self.startDate,
        endDate: self.endDate,
        startTime: self.startTime,
        endTime: self.endTime,
        dailyWinnersCount: self.dailyWinnersCount,
        description: self.description.trim() || undefined,
        resultsVisibleUntil: self.resultsVisibleUntil || undefined,
      };
      try {
        await self.root.api.quiz.updateQuiz(self.quizId, payload);
        (this as any).navigateBack();
      } catch (err: any) {
        const message =
          err?.response?.data?.message ??
          'Došlo je do greške pri čuvanju kviza.';
        self.root.showFailure(message, null);
      }
    },
    formatResultsVisibleUntil(serverFormat: string) {
      const dateObject = new Date(serverFormat);
      return [
        format(dateObject, 'yyyy-MM-dd'),
        format(dateObject, 'hh:mm'),
      ].join('T');
    },
  }))
  .named('KvizDetaljiModel');

export type KvizDetaljiModelType = Instance<typeof KvizDetaljiModel>;
